import Phaser from "phaser";
import { FRUITS, STARTING_FRUIT_MAX, fruitTextureKey } from "../game/fruits";
import { loadBestScore, saveBestScore } from "../game/storage";

const IS_PHONE_PORTRAIT =
  window.matchMedia("(orientation: portrait) and (max-width: 700px)").matches;
const GAME_WIDTH = IS_PHONE_PORTRAIT ? 520 : 960;
const GAME_HEIGHT = IS_PHONE_PORTRAIT ? 900 : 720;

const BOARD = IS_PHONE_PORTRAIT
  ? ({
      x: 44,
      y: 148,
      width: 432,
      height: 680,
      wall: 18
    } as const)
  : ({
      x: 264,
      y: 88,
      width: 432,
      height: 598,
      wall: 18
    } as const);

const INNER_LEFT = BOARD.x + BOARD.wall;
const INNER_RIGHT = BOARD.x + BOARD.width - BOARD.wall;
const INNER_BOTTOM = BOARD.y + BOARD.height - BOARD.wall;
const SPAWN_Y = BOARD.y + 38;
const LOSS_Y = BOARD.y + 78;
const DROP_COOLDOWN_MS = 590;
const LOSS_GRACE_MS = 2200;
const NEW_FRUIT_GRACE_MS = 1550;

type FruitImage = Phaser.Physics.Matter.Image;
type CollisionStartEvent = Phaser.Physics.Matter.Events.CollisionStartEvent;
type HudButton = {
  base: Phaser.GameObjects.Rectangle;
  label: Phaser.GameObjects.Text;
};

export class SuikaScene extends Phaser.Scene {
  private boardGraphics!: Phaser.GameObjects.Graphics;
  private aimGraphics!: Phaser.GameObjects.Graphics;
  private dangerGraphics!: Phaser.GameObjects.Graphics;
  private currentGhost!: Phaser.GameObjects.Image;
  private nextPreview!: Phaser.GameObjects.Image;
  private holdPreview!: Phaser.GameObjects.Image;
  private holdEmptyText!: Phaser.GameObjects.Text;
  private scoreText!: Phaser.GameObjects.Text;
  private bestText!: Phaser.GameObjects.Text;
  private fruitCountText!: Phaser.GameObjects.Text;
  private dangerText!: Phaser.GameObjects.Text;
  private soundButton!: HudButton;
  private holdButton!: HudButton;
  private gameOverLayer?: Phaser.GameObjects.Container;
  private activeFruits = new Set<FruitImage>();
  private pendingMergePairs: Array<[FruitImage, FruitImage]> = [];
  private score = 0;
  private bestScore = 0;
  private currentLevel = 0;
  private nextLevel = 1;
  private holdLevel: number | null = null;
  private holdLocked = false;
  private readyToDrop = true;
  private gameOver = false;
  private pointerStartedInBoard = false;
  private aimX = BOARD.x + BOARD.width / 2;
  private dangerSince: number | null = null;
  private audioContext?: AudioContext;
  private soundEnabled = true;
  private cursors?: Phaser.Types.Input.Keyboard.CursorKeys;
  private keyA?: Phaser.Input.Keyboard.Key;
  private keyD?: Phaser.Input.Keyboard.Key;

  constructor() {
    super("suika");
  }

  create(): void {
    this.bestScore = loadBestScore();
    this.matter.world.setGravity(0, 1.15);

    this.createGeneratedTextures();
    this.createBoardArt();
    this.createWalls();
    this.createHud();
    this.createInput();
    this.resetGame();
  }

  update(time: number, delta: number): void {
    this.processPendingMerges();
    this.updateKeyboardAim(delta);
    this.updateAimVisual();
    this.updateLossState(time);
  }

  private createGeneratedTextures(): void {
    if (this.textures.exists(fruitTextureKey(0))) {
      return;
    }

    FRUITS.forEach((fruit, level) => {
      const size = Math.ceil(fruit.radius * 2 + 18);
      const center = size / 2;
      const graphics = this.add.graphics();

      graphics.fillStyle(0x000000, 0.14);
      graphics.fillCircle(center + fruit.radius * 0.12, center + fruit.radius * 0.14, fruit.radius);

      graphics.fillStyle(fruit.shadow, 1);
      graphics.fillCircle(center, center, fruit.radius + 2);
      graphics.fillStyle(fruit.color, 1);
      graphics.fillCircle(center, center, fruit.radius);

      if (fruit.stripe) {
        graphics.lineStyle(Math.max(5, fruit.radius * 0.11), fruit.stripe, 0.65);
        for (let index = -2; index <= 2; index += 1) {
          const offset = index * fruit.radius * 0.28;
          graphics.beginPath();
          graphics.moveTo(center + offset - fruit.radius * 0.14, center - fruit.radius * 0.78);
          graphics.lineTo(center + offset + fruit.radius * 0.12, center - fruit.radius * 0.26);
          graphics.lineTo(center + offset + fruit.radius * 0.1, center + fruit.radius * 0.24);
          graphics.lineTo(center + offset - fruit.radius * 0.2, center + fruit.radius * 0.78);
          graphics.strokePath();
        }
      }

      graphics.fillStyle(fruit.accent, 0.82);
      graphics.fillCircle(center - fruit.radius * 0.35, center - fruit.radius * 0.38, fruit.radius * 0.24);
      graphics.fillStyle(0xffffff, 0.48);
      graphics.fillCircle(center - fruit.radius * 0.43, center - fruit.radius * 0.48, fruit.radius * 0.09);

      graphics.lineStyle(Math.max(2, fruit.radius * 0.035), 0xffffff, 0.23);
      graphics.strokeCircle(center - fruit.radius * 0.05, center - fruit.radius * 0.04, fruit.radius * 0.84);
      graphics.lineStyle(Math.max(3, fruit.radius * 0.045), fruit.shadow, 0.22);
      graphics.strokeCircle(center, center, fruit.radius);

      graphics.fillStyle(0x5c8f3c, 1);
      graphics.fillEllipse(
        center + fruit.radius * 0.18,
        center - fruit.radius * 0.86,
        fruit.radius * 0.46,
        fruit.radius * 0.22
      );
      graphics.fillStyle(0x7a5b34, 1);
      graphics.fillRoundedRect(
        center - fruit.radius * 0.04,
        center - fruit.radius * 1.03,
        fruit.radius * 0.09,
        fruit.radius * 0.25,
        fruit.radius * 0.04
      );

      graphics.generateTexture(fruitTextureKey(level), size, size);
      graphics.destroy();
    });

    const spark = this.add.graphics();
    spark.fillStyle(0xffffff, 1);
    spark.fillCircle(8, 8, 8);
    spark.generateTexture("spark", 16, 16);
    spark.destroy();
  }

  private createBoardArt(): void {
    this.boardGraphics = this.add.graphics().setDepth(0);

    this.boardGraphics.fillGradientStyle(0xfffbf3, 0xfffbf3, 0xf1dcc4, 0xdad0c3, 1);
    this.boardGraphics.fillRect(0, 0, GAME_WIDTH, GAME_HEIGHT);

    this.boardGraphics.fillStyle(0xe6d6bf, 0.78);
    if (IS_PHONE_PORTRAIT) {
      this.boardGraphics.fillRoundedRect(18, 72, 484, 54, 18);
      this.boardGraphics.fillRoundedRect(18, 840, 484, 48, 18);
    } else {
      this.boardGraphics.fillRoundedRect(34, 74, 184, 568, 22);
      this.boardGraphics.fillRoundedRect(742, 74, 184, 568, 22);
    }

    this.boardGraphics.fillStyle(0xfffaf1, 0.92);
    this.boardGraphics.fillRoundedRect(BOARD.x, BOARD.y, BOARD.width, BOARD.height, 24);
    this.boardGraphics.lineStyle(5, 0x6f4f35, 1);
    this.boardGraphics.strokeRoundedRect(BOARD.x, BOARD.y, BOARD.width, BOARD.height, 24);

    this.boardGraphics.fillStyle(0x8e6744, 1);
    this.boardGraphics.fillRoundedRect(BOARD.x, BOARD.y, BOARD.wall, BOARD.height, 12);
    this.boardGraphics.fillRoundedRect(
      BOARD.x + BOARD.width - BOARD.wall,
      BOARD.y,
      BOARD.wall,
      BOARD.height,
      12
    );
    this.boardGraphics.fillRoundedRect(
      BOARD.x,
      BOARD.y + BOARD.height - BOARD.wall,
      BOARD.width,
      BOARD.wall,
      12
    );

    this.boardGraphics.lineStyle(2, 0xe15f52, 0.62);
    for (let x = INNER_LEFT + 4; x < INNER_RIGHT; x += 20) {
      this.boardGraphics.lineBetween(x, LOSS_Y, Math.min(x + 10, INNER_RIGHT), LOSS_Y);
    }

    this.add
      .text(GAME_WIDTH / 2, IS_PHONE_PORTRAIT ? 22 : 34, "Suika Phaser", {
        fontFamily: "Inter, system-ui, sans-serif",
        fontSize: IS_PHONE_PORTRAIT ? "24px" : "34px",
        color: "#433322",
        fontStyle: "800"
      })
      .setOrigin(0.5)
      .setDepth(3);

    this.add
      .text(GAME_WIDTH / 2, IS_PHONE_PORTRAIT ? 50 : 66, "Matter physics fruit merge", {
        fontFamily: "Inter, system-ui, sans-serif",
        fontSize: IS_PHONE_PORTRAIT ? "12px" : "14px",
        color: "#7c6144",
        fontStyle: "700"
      })
      .setOrigin(0.5)
      .setDepth(3);
  }

  private createWalls(): void {
    const options: Phaser.Types.Physics.Matter.MatterBodyConfig = {
      isStatic: true,
      restitution: 0.02,
      friction: 0.8,
      label: "wall"
    };

    this.matter.add.rectangle(
      BOARD.x + BOARD.wall / 2,
      BOARD.y + BOARD.height / 2,
      BOARD.wall,
      BOARD.height,
      options
    );
    this.matter.add.rectangle(
      BOARD.x + BOARD.width - BOARD.wall / 2,
      BOARD.y + BOARD.height / 2,
      BOARD.wall,
      BOARD.height,
      options
    );
    this.matter.add.rectangle(
      BOARD.x + BOARD.width / 2,
      BOARD.y + BOARD.height - BOARD.wall / 2,
      BOARD.width,
      BOARD.wall,
      options
    );
  }

  private createHud(): void {
    if (IS_PHONE_PORTRAIT) {
      this.addPanelTitle(72, 84, "Score");
      this.scoreText = this.addValueText(72, 111, "0", 28);
      this.addPanelTitle(172, 84, "Best");
      this.bestText = this.addValueText(172, 111, `${this.bestScore}`, 24);
      this.addPanelTitle(270, 84, "Next");
      this.nextPreview = this.add.image(270, 111, fruitTextureKey(0)).setDepth(5);
      this.addPanelTitle(370, 84, "Hold");
      this.holdPreview = this.add.image(370, 111, fruitTextureKey(0)).setDepth(5).setVisible(false);
      this.holdEmptyText = this.add
        .text(370, 111, "Empty", {
          fontFamily: "Inter, system-ui, sans-serif",
          fontSize: "12px",
          color: "#8c7560",
          fontStyle: "700"
        })
        .setOrigin(0.5)
        .setDepth(5);
      this.holdButton = this.createButton(450, 111, 70, 30, "Hold", () => this.tryHold());

      this.addPanelTitle(72, 858, "Fruit");
      this.fruitCountText = this.addValueText(72, 880, "0", 20);
      this.soundButton = this.createButton(220, 862, 124, 34, "Sound", () => this.toggleSound());
      this.createButton(372, 862, 124, 34, "Restart", () => this.resetGame());
    } else {
      this.addPanelTitle(126, 112, "Score");
      this.scoreText = this.addValueText(126, 154, "0", 42);
      this.addPanelTitle(126, 228, "Best");
      this.bestText = this.addValueText(126, 268, `${this.bestScore}`, 32);
      this.addPanelTitle(126, 354, "Fruit");
      this.fruitCountText = this.addValueText(126, 392, "0", 28);

      this.soundButton = this.createButton(126, 594, 132, 42, "Sound On", () => this.toggleSound());
      this.createButton(126, 648, 132, 42, "Restart", () => this.resetGame());

      this.addPanelTitle(834, 112, "Next");
      this.nextPreview = this.add.image(834, 176, fruitTextureKey(0)).setDepth(5);
      this.addPanelTitle(834, 292, "Hold");
      this.holdPreview = this.add.image(834, 350, fruitTextureKey(0)).setDepth(5).setVisible(false);
      this.holdEmptyText = this.add
        .text(834, 350, "Empty", {
          fontFamily: "Inter, system-ui, sans-serif",
          fontSize: "18px",
          color: "#8c7560",
          fontStyle: "700"
        })
        .setOrigin(0.5)
        .setDepth(5);
      this.holdButton = this.createButton(834, 420, 132, 42, "Hold", () => this.tryHold());

      this.addPanelTitle(834, 496, "Chain");
      FRUITS.forEach((fruit, level) => {
        const column = level % 2;
        const row = Math.floor(level / 2);
        const icon = this.add.image(792 + column * 74, 534 + row * 31, fruitTextureKey(level));
        icon.setScale(Math.min(0.32, 24 / fruit.radius)).setDepth(5);
      });
    }

    this.dangerGraphics = this.add.graphics().setDepth(6);
    this.dangerText = this.add
      .text(GAME_WIDTH / 2, LOSS_Y - 22, "DANGER", {
        fontFamily: "Inter, system-ui, sans-serif",
        fontSize: "18px",
        color: "#d2463a",
        fontStyle: "900"
      })
      .setOrigin(0.5)
      .setDepth(7)
      .setVisible(false);

    this.aimGraphics = this.add.graphics().setDepth(7);
    this.currentGhost = this.add.image(this.aimX, SPAWN_Y, fruitTextureKey(0));
    this.currentGhost.setAlpha(0.62).setDepth(8);
  }

  private createInput(): void {
    this.input.on("pointermove", (pointer: Phaser.Input.Pointer) => {
      if (!this.gameOver && this.pointerInsideBoard(pointer)) {
        this.setAim(pointer.worldX);
      }
    });

    this.input.on("pointerdown", (pointer: Phaser.Input.Pointer) => {
      this.pointerStartedInBoard = !this.gameOver && this.pointerInsideBoard(pointer);
      if (this.pointerStartedInBoard) {
        this.setAim(pointer.worldX);
      }
    });

    this.input.on("pointerup", (pointer: Phaser.Input.Pointer) => {
      if (this.pointerStartedInBoard) {
        this.setAim(pointer.worldX);
        this.dropCurrent();
      }
      this.pointerStartedInBoard = false;
    });

    this.matter.world.on(
      "collisionstart",
      (event: CollisionStartEvent) => this.handleCollisionStart(event)
    );

    const keyboard = this.input.keyboard;
    if (!keyboard) {
      return;
    }

    this.cursors = keyboard.createCursorKeys();
    this.keyA = keyboard.addKey(Phaser.Input.Keyboard.KeyCodes.A);
    this.keyD = keyboard.addKey(Phaser.Input.Keyboard.KeyCodes.D);

    keyboard.on("keydown-SPACE", () => this.dropCurrent());
    keyboard.on("keydown-ENTER", () => this.dropCurrent());
    keyboard.on("keydown-DOWN", () => this.dropCurrent());
    keyboard.on("keydown-H", () => this.tryHold());
    keyboard.on("keydown-C", () => this.tryHold());
    keyboard.on("keydown-R", () => this.resetGame());
    keyboard.on("keydown-M", () => this.toggleSound());
  }

  private resetGame(): void {
    this.activeFruits.forEach((fruit) => {
      this.tweens.killTweensOf(fruit);
      fruit.destroy();
    });
    this.activeFruits.clear();
    this.pendingMergePairs = [];
    this.score = 0;
    this.currentLevel = this.randomDropLevel();
    this.nextLevel = this.randomDropLevel();
    this.holdLevel = null;
    this.holdLocked = false;
    this.readyToDrop = true;
    this.gameOver = false;
    this.pointerStartedInBoard = false;
    this.dangerSince = null;
    this.aimX = BOARD.x + BOARD.width / 2;

    this.gameOverLayer?.destroy(true);
    this.gameOverLayer = undefined;
    this.cameras.main.resetFX();

    this.refreshHud();
    this.refreshGhost();
    this.updateAimVisual();
    this.updateDangerPresentation(0);
  }

  private randomDropLevel(): number {
    return Phaser.Math.Between(0, STARTING_FRUIT_MAX);
  }

  private setAim(x: number): void {
    const radius = FRUITS[this.currentLevel].radius;
    this.aimX = Phaser.Math.Clamp(x, INNER_LEFT + radius + 6, INNER_RIGHT - radius - 6);
    this.refreshGhost();
  }

  private dropCurrent(): void {
    if (!this.readyToDrop || this.gameOver) {
      return;
    }

    const droppedLevel = this.currentLevel;
    const fruit = this.createFruit(this.aimX, SPAWN_Y, droppedLevel);
    fruit.setVelocity(0, 1.2);
    fruit.setAngularVelocity(Phaser.Math.FloatBetween(-0.018, 0.018));

    this.readyToDrop = false;
    this.currentGhost.setVisible(false);
    this.playTone("drop", droppedLevel);

    this.currentLevel = this.nextLevel;
    this.nextLevel = this.randomDropLevel();
    this.holdLocked = false;
    this.refreshHud();

    this.time.delayedCall(DROP_COOLDOWN_MS, () => {
      if (!this.gameOver) {
        this.readyToDrop = true;
        this.setAim(this.aimX);
        this.refreshHud();
        this.refreshGhost();
      }
    });
  }

  private tryHold(): void {
    if (!this.readyToDrop || this.gameOver || this.holdLocked) {
      this.bumpButton(this.holdButton, 0xe2b9a3);
      return;
    }

    if (this.holdLevel === null) {
      this.holdLevel = this.currentLevel;
      this.currentLevel = this.nextLevel;
      this.nextLevel = this.randomDropLevel();
    } else {
      const swapLevel = this.currentLevel;
      this.currentLevel = this.holdLevel;
      this.holdLevel = swapLevel;
    }

    this.holdLocked = true;
    this.setAim(this.aimX);
    this.refreshHud();
    this.refreshGhost();
    this.bumpButton(this.holdButton, 0xc3ddb0);
    this.playTone("hold", this.currentLevel);
  }

  private createFruit(x: number, y: number, level: number): FruitImage {
    const spec = FRUITS[level];
    const fruit = this.matter.add.image(x, y, fruitTextureKey(level)) as FruitImage;
    fruit.setCircle(spec.radius);
    fruit.setBounce(0.12);
    fruit.setFriction(0.06);
    fruit.setFrictionAir(0.0015);
    fruit.setDensity(0.0009 + level * 0.00008);
    fruit.setDepth(10 + level);
    fruit.setData("level", level);
    fruit.setData("bornAt", this.time.now);
    fruit.setData("merging", false);
    fruit.setAlpha(0.72);

    this.tweens.add({
      targets: fruit,
      alpha: 1,
      ease: "Quad.Out",
      duration: 160
    });

    this.activeFruits.add(fruit);
    return fruit;
  }

  private handleCollisionStart(event: CollisionStartEvent): void {
    for (const pair of event.pairs) {
      const fruitA = this.fruitFromBody(pair.bodyA);
      const fruitB = this.fruitFromBody(pair.bodyB);

      if (!fruitA || !fruitB || !this.canMerge(fruitA, fruitB)) {
        continue;
      }

      fruitA.setData("merging", true);
      fruitB.setData("merging", true);
      this.pendingMergePairs.push([fruitA, fruitB]);
    }
  }

  private fruitFromBody(body: MatterJS.BodyType): FruitImage | undefined {
    const withGameObject = body as MatterJS.BodyType & {
      gameObject?: Phaser.GameObjects.GameObject;
    };
    const fruit = withGameObject.gameObject as FruitImage | undefined;
    return fruit && this.activeFruits.has(fruit) ? fruit : undefined;
  }

  private canMerge(fruitA: FruitImage, fruitB: FruitImage): boolean {
    if (fruitA === fruitB || fruitA.getData("merging") || fruitB.getData("merging")) {
      return false;
    }

    const levelA = fruitA.getData("level") as number;
    const levelB = fruitB.getData("level") as number;
    return levelA === levelB && levelA < FRUITS.length - 1;
  }

  private processPendingMerges(): void {
    if (this.pendingMergePairs.length === 0) {
      return;
    }

    const pairs = this.pendingMergePairs.splice(0);
    pairs.forEach(([fruitA, fruitB]) => {
      if (!this.activeFruits.has(fruitA) || !this.activeFruits.has(fruitB)) {
        return;
      }

      const level = fruitA.getData("level") as number;
      if (!this.canMergeDespiteFlag(fruitA, fruitB, level)) {
        return;
      }

      this.mergeFruits(fruitA, fruitB, level + 1);
    });
  }

  private canMergeDespiteFlag(fruitA: FruitImage, fruitB: FruitImage, expectedLevel: number): boolean {
    const levelA = fruitA.getData("level") as number;
    const levelB = fruitB.getData("level") as number;
    return levelA === expectedLevel && levelB === expectedLevel && expectedLevel < FRUITS.length - 1;
  }

  private mergeFruits(fruitA: FruitImage, fruitB: FruitImage, nextLevel: number): void {
    const bodyA = fruitA.body as MatterJS.BodyType;
    const bodyB = fruitB.body as MatterJS.BodyType;
    const x = (fruitA.x + fruitB.x) / 2;
    const y = (fruitA.y + fruitB.y) / 2;
    const velocityX = (bodyA.velocity.x + bodyB.velocity.x) * 0.34;
    const velocityY = Math.min(-1.15, (bodyA.velocity.y + bodyB.velocity.y) * 0.18);

    this.tweens.killTweensOf(fruitA);
    this.tweens.killTweensOf(fruitB);
    this.activeFruits.delete(fruitA);
    this.activeFruits.delete(fruitB);
    fruitA.destroy();
    fruitB.destroy();

    const merged = this.createFruit(x, y, nextLevel);
    merged.setVelocity(velocityX, velocityY);
    merged.setAngularVelocity(Phaser.Math.FloatBetween(-0.045, 0.045));

    this.score += FRUITS[nextLevel].score;
    if (this.score > this.bestScore) {
      this.bestScore = this.score;
      saveBestScore(this.bestScore);
    }

    this.spawnMergeBurst(x, y, nextLevel);
    this.spawnScorePop(x, y, FRUITS[nextLevel].score);
    this.cameras.main.shake(70, 0.0025 + nextLevel * 0.00025);
    this.playTone("merge", nextLevel);
    this.refreshHud();
  }

  private updateKeyboardAim(delta: number): void {
    if (!this.readyToDrop || this.gameOver) {
      return;
    }

    const left = Boolean(this.cursors?.left?.isDown || this.keyA?.isDown);
    const right = Boolean(this.cursors?.right?.isDown || this.keyD?.isDown);
    const direction = Number(right) - Number(left);

    if (direction !== 0) {
      this.setAim(this.aimX + direction * delta * 0.32);
    }
  }

  private updateAimVisual(): void {
    this.aimGraphics.clear();

    if (!this.readyToDrop || this.gameOver) {
      return;
    }

    const radius = FRUITS[this.currentLevel].radius;
    this.aimGraphics.lineStyle(2, 0x5b78a3, 0.48);
    for (let y = SPAWN_Y + radius + 8; y < INNER_BOTTOM - 12; y += 18) {
      this.aimGraphics.lineBetween(this.aimX, y, this.aimX, Math.min(y + 9, INNER_BOTTOM - 12));
    }
    this.aimGraphics.fillStyle(0x5b78a3, 0.8);
    this.aimGraphics.fillTriangle(
      this.aimX - 8,
      INNER_BOTTOM - 9,
      this.aimX + 8,
      INNER_BOTTOM - 9,
      this.aimX,
      INNER_BOTTOM - 1
    );
  }

  private updateLossState(time: number): void {
    if (this.gameOver) {
      return;
    }

    let threatened = false;
    this.activeFruits.forEach((fruit) => {
      if (threatened || fruit.getData("merging")) {
        return;
      }

      const bornAt = fruit.getData("bornAt") as number;
      if (time - bornAt < NEW_FRUIT_GRACE_MS) {
        return;
      }

      const level = fruit.getData("level") as number;
      const top = fruit.y - FRUITS[level].radius;
      if (top < LOSS_Y && fruit.y > BOARD.y + 8) {
        threatened = true;
      }
    });

    if (!threatened) {
      this.dangerSince = null;
      this.updateDangerPresentation(0);
      return;
    }

    if (this.dangerSince === null) {
      this.dangerSince = time;
    }

    const progress = Phaser.Math.Clamp((time - this.dangerSince) / LOSS_GRACE_MS, 0, 1);
    this.updateDangerPresentation(progress);

    if (progress >= 1) {
      this.endGame();
    }
  }

  private updateDangerPresentation(progress: number): void {
    this.dangerGraphics.clear();
    this.dangerText.setVisible(progress > 0);

    if (progress <= 0) {
      return;
    }

    this.dangerText.setAlpha(0.45 + Math.sin(this.time.now * 0.018) * 0.22 + progress * 0.32);
    this.dangerGraphics.lineStyle(6, 0xd2463a, 0.16 + progress * 0.44);
    this.dangerGraphics.lineBetween(INNER_LEFT, LOSS_Y, INNER_RIGHT, LOSS_Y);
    this.dangerGraphics.lineStyle(3, 0xd2463a, 0.58);
    this.dangerGraphics.lineBetween(INNER_LEFT, LOSS_Y, INNER_LEFT + (INNER_RIGHT - INNER_LEFT) * progress, LOSS_Y);
  }

  private endGame(): void {
    if (this.gameOver) {
      return;
    }

    this.gameOver = true;
    this.readyToDrop = false;
    this.currentGhost.setVisible(false);
    this.aimGraphics.clear();
    this.updateDangerPresentation(1);
    this.playTone("lose");
    this.cameras.main.shake(260, 0.006);
    this.refreshHud();
    this.showGameOverLayer();
  }

  private showGameOverLayer(): void {
    this.gameOverLayer?.destroy(true);

    const layer = this.add.container(0, 0).setDepth(80);
    const shade = this.add.rectangle(GAME_WIDTH / 2, GAME_HEIGHT / 2, GAME_WIDTH, GAME_HEIGHT, 0x1f211f, 0.4);
    shade.setInteractive();

    const panel = this.add.rectangle(GAME_WIDTH / 2, 360, 390, 268, 0xfffbf3, 0.97);
    panel.setStrokeStyle(4, 0x6f4f35, 1);

    const title = this.add
      .text(GAME_WIDTH / 2, 275, "Game Over", {
        fontFamily: "Inter, system-ui, sans-serif",
        fontSize: "38px",
        color: "#3e3022",
        fontStyle: "900"
      })
      .setOrigin(0.5);

    const score = this.add
      .text(GAME_WIDTH / 2, 328, `Score ${this.score}`, {
        fontFamily: "Inter, system-ui, sans-serif",
        fontSize: "24px",
        color: "#6b5138",
        fontStyle: "800"
      })
      .setOrigin(0.5);

    const best = this.add
      .text(GAME_WIDTH / 2, 362, `Best ${this.bestScore}`, {
        fontFamily: "Inter, system-ui, sans-serif",
        fontSize: "18px",
        color: "#8a7058",
        fontStyle: "700"
      })
      .setOrigin(0.5);

    const buttonBase = this.add.rectangle(GAME_WIDTH / 2, 438, 176, 52, 0x3f7f64, 1);
    buttonBase.setStrokeStyle(2, 0x244e3e, 0.55);
    buttonBase.setInteractive({ useHandCursor: true });
    const buttonLabel = this.add
      .text(GAME_WIDTH / 2, 438, "Play Again", {
        fontFamily: "Inter, system-ui, sans-serif",
        fontSize: "20px",
        color: "#fff8ea",
        fontStyle: "900"
      })
      .setOrigin(0.5);

    buttonBase.on("pointerover", () => buttonBase.setFillStyle(0x4b946e, 1));
    buttonBase.on("pointerout", () => buttonBase.setFillStyle(0x3f7f64, 1));
    buttonBase.on("pointerdown", () => this.resetGame());
    buttonLabel.setInteractive({ useHandCursor: true }).on("pointerdown", () => this.resetGame());

    layer.add([shade, panel, title, score, best, buttonBase, buttonLabel]);
    this.gameOverLayer = layer;
  }

  private refreshHud(): void {
    this.scoreText.setText(`${this.score}`);
    this.bestText.setText(`${this.bestScore}`);
    this.fruitCountText.setText(`${this.activeFruits.size}`);
    this.soundButton.label.setText(this.soundEnabled ? (IS_PHONE_PORTRAIT ? "Sound" : "Sound On") : (IS_PHONE_PORTRAIT ? "Mute" : "Sound Off"));
    this.holdButton.label.setText(this.holdLocked ? "Held" : "Hold");

    this.nextPreview.setTexture(fruitTextureKey(this.nextLevel));
    this.nextPreview.setScale(Math.min(IS_PHONE_PORTRAIT ? 0.42 : 0.9, (IS_PHONE_PORTRAIT ? 34 : 76) / FRUITS[this.nextLevel].radius));

    if (this.holdLevel === null) {
      this.holdPreview.setVisible(false);
      this.holdEmptyText.setVisible(true);
    } else {
      this.holdPreview.setTexture(fruitTextureKey(this.holdLevel));
      this.holdPreview.setScale(Math.min(IS_PHONE_PORTRAIT ? 0.42 : 0.85, (IS_PHONE_PORTRAIT ? 34 : 70) / FRUITS[this.holdLevel].radius));
      this.holdPreview.setVisible(true);
      this.holdEmptyText.setVisible(false);
    }
  }

  private refreshGhost(): void {
    if (!this.currentGhost) {
      return;
    }

    this.currentGhost
      .setTexture(fruitTextureKey(this.currentLevel))
      .setPosition(this.aimX, SPAWN_Y)
      .setAlpha(this.readyToDrop && !this.gameOver ? 0.62 : 0)
      .setVisible(this.readyToDrop && !this.gameOver)
      .setScale(1);
  }

  private spawnMergeBurst(x: number, y: number, level: number): void {
    const color = FRUITS[level].accent;
    const count = Phaser.Math.Clamp(10 + level * 2, 10, 28);

    for (let index = 0; index < count; index += 1) {
      const dot = this.add.circle(x, y, Phaser.Math.Between(3, 7), color, 0.9).setDepth(35);
      const angle = Phaser.Math.FloatBetween(0, Math.PI * 2);
      const distance = Phaser.Math.Between(30, 90 + level * 4);

      this.tweens.add({
        targets: dot,
        x: x + Math.cos(angle) * distance,
        y: y + Math.sin(angle) * distance,
        scaleX: 0.15,
        scaleY: 0.15,
        alpha: 0,
        ease: "Quad.Out",
        duration: 360 + level * 18,
        onComplete: () => dot.destroy()
      });
    }
  }

  private spawnScorePop(x: number, y: number, value: number): void {
    const text = this.add
      .text(x, y - 18, `+${value}`, {
        fontFamily: "Inter, system-ui, sans-serif",
        fontSize: "22px",
        color: "#3e3022",
        fontStyle: "900",
        stroke: "#fff8ea",
        strokeThickness: 5
      })
      .setOrigin(0.5)
      .setDepth(45);

    this.tweens.add({
      targets: text,
      y: y - 64,
      alpha: 0,
      ease: "Cubic.Out",
      duration: 720,
      onComplete: () => text.destroy()
    });
  }

  private toggleSound(): void {
    this.soundEnabled = !this.soundEnabled;
    this.refreshHud();
    this.bumpButton(this.soundButton, this.soundEnabled ? 0xc3ddb0 : 0xe2b9a3);
    if (this.soundEnabled) {
      this.playTone("hold", 2);
    }
  }

  private playTone(kind: "drop" | "merge" | "hold" | "lose", level = 0): void {
    if (!this.soundEnabled) {
      return;
    }

    const AudioContextCtor =
      window.AudioContext ??
      (window as Window & { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;

    if (!AudioContextCtor) {
      return;
    }

    this.audioContext ??= new AudioContextCtor();
    if (this.audioContext.state === "suspended") {
      this.audioContext.resume().catch(() => undefined);
    }

    const now = this.audioContext.currentTime;
    const oscillator = this.audioContext.createOscillator();
    const gain = this.audioContext.createGain();
    const baseFrequency = kind === "lose" ? 150 : kind === "merge" ? 330 + level * 38 : 220 + level * 24;

    oscillator.type = kind === "lose" ? "sawtooth" : kind === "hold" ? "triangle" : "sine";
    oscillator.frequency.setValueAtTime(baseFrequency, now);
    if (kind === "merge") {
      oscillator.frequency.exponentialRampToValueAtTime(baseFrequency * 1.55, now + 0.12);
    } else if (kind === "lose") {
      oscillator.frequency.exponentialRampToValueAtTime(72, now + 0.32);
    }

    gain.gain.setValueAtTime(0.0001, now);
    gain.gain.exponentialRampToValueAtTime(kind === "lose" ? 0.085 : 0.045, now + 0.018);
    gain.gain.exponentialRampToValueAtTime(0.0001, now + (kind === "lose" ? 0.38 : 0.16));

    oscillator.connect(gain);
    gain.connect(this.audioContext.destination);
    oscillator.start(now);
    oscillator.stop(now + (kind === "lose" ? 0.4 : 0.18));
  }

  private addPanelTitle(x: number, y: number, text: string): Phaser.GameObjects.Text {
    return this.add
      .text(x, y, text.toUpperCase(), {
        fontFamily: "Inter, system-ui, sans-serif",
        fontSize: "15px",
        color: "#72583d",
        fontStyle: "900"
      })
      .setOrigin(0.5)
      .setDepth(5);
  }

  private addValueText(
    x: number,
    y: number,
    text: string,
    size: number
  ): Phaser.GameObjects.Text {
    return this.add
      .text(x, y, text, {
        fontFamily: "Inter, system-ui, sans-serif",
        fontSize: `${size}px`,
        color: "#392d21",
        fontStyle: "900"
      })
      .setOrigin(0.5)
      .setDepth(5);
  }

  private createButton(
    x: number,
    y: number,
    width: number,
    height: number,
    label: string,
    onClick: () => void
  ): HudButton {
    const base = this.add.rectangle(x, y, width, height, 0xfff5e8, 1).setDepth(5);
    base.setStrokeStyle(2, 0x8b6a49, 0.45);
    base.setInteractive({ useHandCursor: true });
    const text = this.add
      .text(x, y, label, {
        fontFamily: "Inter, system-ui, sans-serif",
        fontSize: "16px",
        color: "#493824",
        fontStyle: "900"
      })
      .setOrigin(0.5)
      .setDepth(6)
      .setInteractive({ useHandCursor: true });

    const press = () => {
      this.tweens.add({
        targets: [base, text],
        scaleX: 0.96,
        scaleY: 0.96,
        yoyo: true,
        duration: 76
      });
      onClick();
    };

    base.on("pointerover", () => base.setFillStyle(0xffffff, 1));
    base.on("pointerout", () => base.setFillStyle(0xfff5e8, 1));
    base.on("pointerdown", press);
    text.on("pointerdown", press);

    return { base, label: text };
  }

  private bumpButton(button: HudButton, color: number): void {
    button.base.setFillStyle(color, 1);
    this.time.delayedCall(150, () => button.base.setFillStyle(0xfff5e8, 1));
  }

  private pointerInsideBoard(pointer: Phaser.Input.Pointer): boolean {
    return (
      pointer.worldX >= INNER_LEFT &&
      pointer.worldX <= INNER_RIGHT &&
      pointer.worldY >= BOARD.y - 26 &&
      pointer.worldY <= INNER_BOTTOM
    );
  }
}
